import React from 'react';
import { useArchitectureStore } from '../../store/useArchitectureStore';
import { WORKFLOWS } from '../../data/workflows';
import { ChevronRight, ChevronLeft, Check, X } from 'lucide-react';

const TaskNavigator: React.FC = () => {
    const { activeTask, nextStep, prevStep, cancelTask, completeTask } = useArchitectureStore();

    if (!activeTask) return null;

    const workflow = WORKFLOWS[activeTask.type];
    const currentStep = workflow.steps[activeTask.step];
    const isFirstStep = activeTask.step === 0;
    const isLastStep = activeTask.step === workflow.steps.length - 1;

    return (
        <div className="absolute top-20 left-1/2 -translate-x-1/2 z-40 bg-stone-900/90 backdrop-blur-md border border-stone-800 rounded-2xl shadow-2xl p-4 w-full max-w-2xl flex flex-col gap-4 animate-in fade-in slide-in-from-top-4">
            {/* Header: Task Title & Close */}
            <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                    <span className="text-xs font-bold uppercase tracking-widest text-stone-500">{workflow.label}</span>
                    <h3 className="text-lg font-bold text-zinc-100">{currentStep.label}</h3>
                </div>
                <button
                    onClick={cancelTask}
                    className="p-1 rounded-full hover:bg-white/10 text-stone-500 hover:text-red-400 transition-colors"
                >
                    <X size={18} />
                </button>
            </div>

            {/* Stepper Visualization */}
            <div className="flex items-center w-full gap-2">
                {workflow.steps.map((step, index) => {
                    const isActive = index === activeTask.step;
                    const isCompleted = index < activeTask.step;

                    return (
                        <div key={step.id} className="flex-1 flex flex-col gap-2 group">
                            <div className={`h-1 rounded-full transition-all duration-500 ${isActive ? 'bg-indigo-500 shadow-[0_0_10px_rgba(99,102,241,0.5)]' :
                                    isCompleted ? 'bg-emerald-500' : 'bg-stone-800'
                                }`} />
                            <span className={`text-[10px] font-medium transition-colors text-center ${isActive ? 'text-indigo-400' :
                                    isCompleted ? 'text-emerald-500' : 'text-stone-600'
                                }`}>
                                {step.label}
                            </span>
                        </div>
                    );
                })}
            </div>

            {/* Navigation Controls */}
            <div className="flex items-center justify-between mt-2 pt-2 border-t border-stone-800/50">
                <button
                    onClick={prevStep}
                    disabled={isFirstStep}
                    className={`flex items-center gap-1 px-4 py-2 rounded-lg text-sm font-medium transition-colors ${isFirstStep
                            ? 'text-stone-600 cursor-not-allowed'
                            : 'text-stone-300 hover:bg-white/5 hover:text-white'
                        }`}
                >
                    <ChevronLeft size={16} />
                    Back
                </button>

                {isLastStep ? (
                    <button
                        onClick={completeTask}
                        className="flex items-center gap-2 px-6 py-2 rounded-xl bg-emerald-600 hover:bg-emerald-500 text-white font-bold shadow-lg shadow-emerald-900/20 transition-all hover:scale-105"
                    >
                        <Check size={18} />
                        Finish
                    </button>
                ) : (
                    <button
                        onClick={nextStep}
                        className="flex items-center gap-2 px-6 py-2 rounded-xl bg-indigo-600 hover:bg-indigo-500 text-white font-bold shadow-lg shadow-indigo-900/20 transition-all hover:scale-105"
                    >
                        Next
                        <ChevronRight size={18} />
                    </button>
                )}
            </div>
        </div>
    );
};

export default TaskNavigator;
